import moment from 'moment-timezone'
import { FormattedCalendarMeta } from '@/types/general.types'
import { TIME_FORMATS } from '@/config/dates.config'
import { padNumber } from '@/utils/number.utils'
import { BLACKOUT_DATE_CODE, SPECIAL_EVENT_CODE } from '@/config/calendar.config'

export const isSpecialEventFromCode = (code: string | undefined): boolean => code === SPECIAL_EVENT_CODE

export const isBlackoutDateFromCode = (code: string | undefined): boolean => code === BLACKOUT_DATE_CODE

export const isBlackoutDate = (
  formattedDate: string,
  calendarMeta: FormattedCalendarMeta,
): boolean => calendarMeta[formattedDate]?.isBlackoutDate

export const hasSpecialEvents = (
  formattedDate: string,
  calendarMeta: FormattedCalendarMeta,
): boolean => !!calendarMeta[formattedDate]?.announcements
  .find((announcement) => isSpecialEventFromCode(announcement?.code))

export const shouldCheckForBlackoutDate = (
  theDate: Transformed.Calendar | undefined,
  hasMembership: boolean,
): boolean => (
  !!theDate
    && !theDate.soldOut
    && !theDate.closed
    && hasMembership
)

/**
 * Format a Date to YYYY-MM-DD (with optional time component)
 */
export const formatIncomingDate = (date: Date, withTime = false): string => {
  const d = moment(date)
  const month = padNumber(d.get('month') + 1)
  const day = padNumber(d.get('date'))
  const year = d.get('year')
  return `${year}-${month}-${day}${withTime ? ' 10:00' : ''}`
}

/**
 * Format a date with timezone awareness to YYYY-MM-DD
 */
export const formatDate = (date: Date, timezone: string): string => (
  moment.tz(formatIncomingDate(date, true), timezone).format(TIME_FORMATS.calendarDate)
)

/**
 * Convert a seller-local YYYY-MM-DD string into a browser-local Date anchored at noon.
 * Noon avoids crossing day boundaries when the browser timezone differs from the seller timezone.
 * This keeps Central, Mountain, Pacific, etc. sellers rendering on the intended calendar day
 * in react-datepicker, which operates on browser-local Date instances.
 */
export const getCalendarPickerDate = (date: string): Date => (
  moment(date, TIME_FORMATS.calendarDate)
    .hour(12)
    .minute(0)
    .second(0)
    .millisecond(0)
    .toDate()
)

/**
 * Convert an active UTC datetime into the local Date representation used by the date picker.
 * We first resolve the seller-local calendar day, then map that day to the picker-safe local-noon Date.
 */
export const getCalendarSelectedDate = (
  activeDate: string | null | undefined,
  timezone: string,
): Date | null => (
  activeDate
    ? getCalendarPickerDate(moment.tz(activeDate, timezone).format(TIME_FORMATS.calendarDate))
    : null
)

/**
 * Determine which month the calendar should display.
 * Prefer the selected day so deep links and other programmatic date changes stay in view.
 * Before a day is selected, fall back to the earliest available month.
 */
export const getCalendarDisplayMonth = ({
  activeDate,
  includeDates,
  timezone,
}: {
  activeDate: string | null | undefined
  includeDates: Date[]
  timezone: string
}): Date | null => {
  const selectedDate = getCalendarSelectedDate(activeDate, timezone)
  if (selectedDate) return selectedDate
  if (!includeDates.length) return null
  return includeDates.reduce((earliest, date) => (date < earliest ? date : earliest))
}

/**
 * Convert Transformed.CalendarMeta[] to FormattedCalendarMeta (keyed by date)
 */
export const getCalendarMetaFromList = (list: Transformed.CalendarMeta[]): FormattedCalendarMeta => (
  Object.fromEntries(list.map((meta) => [meta.date, meta]))
)

/**
 * Compute the from/to range for a calendarMeta fetch.
 * Mirrors legacy getCalendarMetaRange:
 * - Current month: start = startOfMonth, end = startOfMonth + 3 months
 * - Future month: start = startOfMonth - 1 month (covers overflow days), end = startOfMonth + 3 months
 */
export const getCalendarMetaRange = (date: moment.Moment): { from: string; to: string } => {
  const startOfMonth = date.clone().startOf('month')
  const isCurrentMonth = startOfMonth.isSame(moment(), 'month')
  const from = isCurrentMonth
    ? startOfMonth.clone().format('MM/DD/YYYY HH:mm')
    : startOfMonth.clone().subtract(1, 'month')
      .format('MM/DD/YYYY HH:mm')
  const to = startOfMonth.clone().add(3, 'month')
    .format('MM/DD/YYYY HH:mm')
  return { from, to }
}

/**
 * Convert available Transformed.Calendar items to Date[] for react-datepicker includeDates.
 * Excludes sold-out and closed dates.
 */
export const getIncludeDates = (
  dates: readonly Transformed.Calendar[],
  timeZone: string,
): Date[] => dates
  .filter((date) => !date.soldOut && !date.closed)
  .map((date) => getCalendarPickerDate(moment.tz(date.dateTime, timeZone).format(TIME_FORMATS.calendarDate)))

const BELOW_CALENDAR_ANNOUNCEMENT_CODES = ['notice']

export const getNoticeAnnouncements = (
  announcements: Array<Nullable<Transformed.Announcement>>,
): Transformed.Announcement[] => (
  announcements
    .filter((a): a is Transformed.Announcement => a !== null)
    .filter((a) => BELOW_CALENDAR_ANNOUNCEMENT_CODES.includes(a.code))
)

export const getInCalendarAnnouncements = (
  announcements: Array<Nullable<Transformed.Announcement>>,
  hasAPMembership: boolean,
): Transformed.Announcement[] => (
  announcements
    .filter((a): a is Transformed.Announcement => a !== null)
    .filter((a) => !BELOW_CALENDAR_ANNOUNCEMENT_CODES.includes(a.code))
    .filter((a) => {
      if (a.code !== BLACKOUT_DATE_CODE) return true
      return isBlackoutDateFromCode(a.code) && hasAPMembership
    })
)
